import React, { useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import Swal from 'sweetalert2';
import { useBoolean } from 'usehooks-ts';
import { FaPlus } from 'react-icons/fa';

import useLoader from 'hooks/useLoader';
import { MenuMaster } from 'models/role';
import { extractAccessToken } from 'helpers/decodeLocalStorage';

import {
    WorkflowMenuMasterFormModal,
    WorkflowMenuMasterTable
} from './components';
import {
    useAddMasterMenu,
    useDeleteMasterMenu,
    useEditMasterMenu,
    useMasterMenu
} from './queries';
import Select from 'react-select';
import { MasterCompany } from '../../../models/company/MasterCompany';
import { useAdminCompanies } from '../../../hooks';
import { Filter } from '@material-ui/icons';

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
        ? error.message
        : 'Unknown error occurred.';

export const WorkflowMenuMasterConfig = () => {
    const loader = useLoader();
    const queryClient = useQueryClient();

    const { adminCompanies, isAllowedAllCompanies } = useAdminCompanies();
    const adminCompanyOptions = useMemo(() => {
        if (!adminCompanies) {
            return undefined;
        }

        const options: MasterCompany[] = [
            {
                sid: 'all',
                company_name: 'เลือกทั้งหมด'
            } as MasterCompany
        ];

        if (isAllowedAllCompanies) {
            options.push({
                sid: '*',
                company_name: '* : SUPER_ADMIN'
            } as MasterCompany);
        }

        return options.concat(adminCompanies);
    }, [adminCompanies, isAllowedAllCompanies]);
    const adminCompanySet = useMemo(
        () =>
            adminCompanies
                ? new Set(adminCompanies.map(company => company.sid))
                : undefined,
        [adminCompanies]
    );

    const [selectedCompany, setSelectedCompany] = useState<MasterCompany>();

    const { data: masterMenu, isFetching: isFetchingMasterMenu } =
        useMasterMenu();
    const filteredMasterMenu = useMemo(
        () =>
            masterMenu?.filter(menu => {
                if (!selectedCompany || selectedCompany.sid === 'all') {
                    return !isAllowedAllCompanies
                        ? adminCompanySet?.has(menu.sid)
                        : true;
                }

                return menu.sid === selectedCompany.sid;
            }),
        [masterMenu, isAllowedAllCompanies, selectedCompany]
    );

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addMasterMenu } = useAddMasterMenu();

    const [selectedEditMenuMasters, setSelectedEditMenuMasters] =
        useState<MenuMaster[]>();
    const isOpenEditModal = !!selectedEditMenuMasters;
    const { mutate: editMasterMenu } = useEditMasterMenu();

    const { mutate: deleteMasterMenu } = useDeleteMasterMenu();

    const handleSubmitAddMenuMaster = (menuMasters: MenuMaster[]) => {
        const requests = menuMasters.map(menu => ({
            ...menu,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }));

        console.log('ngame add requests', requests);

        loader.show();

        requests.forEach(request =>
            addMasterMenu(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    loader.hide();
                    toggleIsOpenCreateModal();

                    await queryClient.invalidateQueries({
                        queryKey: ['master-menu']
                    });
                }
            })
        );
    };

    const handleEditMenuMasters = (menuMasters: MenuMaster[]) => {
        setSelectedEditMenuMasters(menuMasters);
    };

    const handleCloseEditModal = () => {
        setSelectedEditMenuMasters(undefined);
    };

    const handleSubmitEditMenuMaster = async (menuMasters: MenuMaster[]) => {
        if (menuMasters.length === 0 || !filteredMasterMenu) {
            return;
        }

        // Look-ups
        const menuMasterSIDSet = new Set(menuMasters.map(menu => menu.sid));
        const currentMenus = filteredMasterMenu.filter(
            menu => menu.id === menuMasters[0].id
        );
        const currentMenuSIDSet = new Set(currentMenus.map(menu => menu.sid));

        // Delete requests
        const deleteMenuRequests = currentMenus
            .filter(existingMenu => !menuMasterSIDSet.has(existingMenu.sid))
            .map(menu => ({ sid: menu.sid, id: menu.id }));
        const deleteMenuSIDSet = new Set(
            deleteMenuRequests.map(menu => menu.sid)
        );

        // Add requests
        const addMenuRequests = menuMasters
            .filter(menu => !currentMenuSIDSet.has(menu.sid))
            .map(menu => ({
                ...menu,
                created_by:
                    extractAccessToken().citizen_id ||
                    extractAccessToken().username
            }));
        const addMenuSIDSet = new Set(addMenuRequests.map(menu => menu.sid));

        // Edit requests
        const editMenuRequests = menuMasters
            .filter(
                menu =>
                    !deleteMenuSIDSet.has(menu.sid) &&
                    !addMenuSIDSet.has(menu.sid)
            )
            .map(menu => ({
                ...menu,
                updated_by:
                    extractAccessToken().citizen_id ||
                    extractAccessToken().username
            }));

        loader.show();

        try {
            await Promise.all([
                ...deleteMenuRequests.map(request => deleteMasterMenu(request)),
                ...addMenuRequests.map(request => addMasterMenu(request)),
                ...editMenuRequests.map(request => editMasterMenu(request))
            ]);

            loader.hide();

            handleCloseEditModal();

            await Swal.fire({
                title: 'Success!',
                icon: 'success'
            });
            await queryClient.invalidateQueries({ queryKey: ['master-menu'] });
        } catch (error) {
            loader.hide();

            await Swal.fire({
                title: 'Error!',
                icon: 'error',
                text: getErrorMessage(error)
            });
        }
    };

    const handleDeleteMenuMaster = async (menuMasters: MenuMaster[]) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${menuMasters[0]?.id}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = menuMasters.map(menuMaster => ({
            sid: menuMaster.sid,
            id: menuMaster.id
        }));

        console.log('ngame delete requests', requests);

        loader.show();

        requests.forEach(request =>
            deleteMasterMenu(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    loader.hide();
                    await queryClient.invalidateQueries({
                        queryKey: ['master-menu']
                    });
                }
            })
        );
    };

    return (
        <div className="master-data-content">
            <div className="flex justify-between">
                <div
                    className="trapezoid-head"
                    style={{ width: '200px' }}
                >
                    <span>Workflow Menu Master Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="flex items-center gap-1 self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>
            <div className="mat-box">
                <div className="flex justify-between">
                    <div className="trapezoid-head w-fit">
                        <span>Search</span>
                    </div>
                </div>
                <div className="mat-box flex flex-col">
                    <FormGroup className="w-full md:w-1/3">
                        <Label for="sid">Company</Label>
                        <Select
                            options={adminCompanyOptions}
                            value={selectedCompany}
                            getOptionLabel={(company: MasterCompany) =>
                                company.company_name
                            }
                            getOptionValue={(company: MasterCompany) =>
                                company.sid
                            }
                            onChange={setSelectedCompany}
                        />
                    </FormGroup>
                </div>

                <WorkflowMenuMasterTable
                    isPending={isFetchingMasterMenu}
                    menus={filteredMasterMenu}
                    onEdit={handleEditMenuMasters}
                    onDelete={handleDeleteMenuMaster}
                />

                {/* Create modal */}
                <WorkflowMenuMasterFormModal
                    title="Create Menu ID"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddMenuMaster}
                />

                {/* Edit modal */}
                <WorkflowMenuMasterFormModal
                    disabledKeyField
                    title="Edit Menu ID"
                    defaultValues={selectedEditMenuMasters}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditMenuMaster}
                />
            </div>
        </div>
    );
};
